import { fetchV1, fetchAdminV1, fetchTenantAdminV1 } from '../lib/requester/v1';
import { ROLES } from '../helpers/constants';

// Helper function to determine which API endpoint to use based on user role
const getApiEndpoint = (user) => {
  if (user?.roles?.includes(ROLES.SUPERADMIN)) {
    return fetchAdminV1;
  } else if (user?.roles?.includes(ROLES.TENANT_ADMIN)) {
    return fetchTenantAdminV1;
  }
  return fetchAdminV1; // Default fallback
};

/**
 * Get funder outreach data with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-based)
 * @param {number} params.limit - Number of records per page
 * @param {string} params.campaign - Filter by campaign name
 * @param {string} params.funder - Filter by funder name
 * @param {string} params.status - Filter by outreach status
 * @returns {Promise<Object>} Response with data and pagination info
 */
export const getFunderOutreachData = async (params = {}, user = null) => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    // Add filter parameters
    if (params.campaign) queryParams.append('campaign', params.campaign);
    if (params.funder) queryParams.append('funder', params.funder);
    if (params.status) queryParams.append('status', params.status);
    
    const queryString = queryParams.toString();
    const url = `/founder-outreach${queryString ? `?${queryString}` : ''}`;
    
    const apiEndpoint = getApiEndpoint(user);
    console.log('🔍 [DEBUG] Outreach API: Using endpoint:', apiEndpoint === fetchAdminV1 ? 'Admin' : 'TenantAdmin');
    
    const response = await apiEndpoint(url, {
      method: 'GET',
    }, { returnWholeResponse: true });
    
    return response;
  } catch (error) {
    console.error('Error fetching funder outreach data:', error);
    throw error;
  }
};

/**
 * Get outreach statistics for dashboard overview
 * @returns {Promise<Object>} Response with statistics
 */
export const getOutreachStatistics = async () => {
  try {
    const response = await fetchAdminV1('/founder-outreach/statistics', {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Error fetching outreach statistics:', error);
    throw error;
  }
};

/**
 * Get detailed outreach information by ID
 * @param {string} outreachId - Outreach record ID
 * @returns {Promise<Object>} Response with detailed outreach information
 */
export const getOutreachDetails = async (outreachId) => {
  try {
    const response = await fetchAdminV1(`/founder-outreach/${outreachId}`, {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Error fetching outreach details:', error);
    throw error;
  }
};

/**
 * Update outreach status
 * @param {string} outreachId - Outreach record ID
 * @param {Object} updateData - Data to update
 * @param {string} updateData.status - New status
 * @param {string} updateData.notes - Optional notes
 * @returns {Promise<Object>} Response with updated outreach record
 */
export const updateOutreachStatus = async (outreachId, updateData) => {
  try {
    const response = await fetchAdminV1(`/founder-outreach/${outreachId}`, {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
    
    return response;
  } catch (error) {
    console.error('Error updating outreach status:', error);
    throw error;
  }
};

/**
 * Resend outreach email to funder
 * @param {string} outreachId - Outreach record ID
 * @returns {Promise<Object>} Response with email status
 */
export const resendOutreachEmail = async (outreachId) => {
  try {
    const response = await fetchAdminV1(`/founder-outreach/${outreachId}/resend`, {
      method: 'POST',
    });
    
    return response;
  } catch (error) {
    console.error('Error resending outreach email:', error);
    throw error;
  }
};

/**
 * Get founder's outreach data (authenticated founders only)
 * @returns {Promise<Object>} Response with outreach data for the logged-in founder
 */
export const getMyOutreach = async () => {
  try {
    const response = await fetchV1('/founder-outreach/my-outreach', {
      method: 'GET',
    });
    
    return response;
  } catch (error) {
    console.error('Error fetching my outreach:', error);
    throw error;
  }
};
